// File: src/services/media/resolve/resolveMedia.ts
//
// resolveMedia — canonical media resolution (MediaRefV1 -> ResolvedMediaV1)
// ------------------------------------------------------------------------
// Goals:
// - One deterministic resolution path for export/runtime
// - Runtime-installable hooks for registry + firebase customization
// - No hard coupling to app features; dynamic imports where needed

import type {
  MediaRefV1,
  MediaKindV1,
  MediaProviderV1,
  RegistryMediaRefV1,
  FirebaseMediaRefV1,
  StaticMediaRefV1,
  UrlMediaRefV1,
  GcsMediaRefV1,
} from "../contracts/mediaRefV1";

export interface ResolvedMediaV1 {
  v: 1;
  kind: MediaKindV1;
  provider: MediaProviderV1;
  url: string;
  mime?: string;
  /** Original reference (for audit/debug; safe to retain). */
  ref: MediaRefV1;
  /** Optional structured metadata (not required for determinism). */
  meta?: Record<string, any>;
}

export type ResolveMediaFn = (ref: MediaRefV1) => Promise<ResolvedMediaV1>;

export type RegistryResolveResult =
  | string
  | { url: string; mime?: string; meta?: Record<string, any> };

export interface ResolveMediaInstallOptions {
  /**
   * Registry resolver hook (required for provider="registry").
   * You can install multiple by selecting on ref.registry inside the resolver.
   */
  registryResolver?: (ref: RegistryMediaRefV1) => Promise<RegistryResolveResult>;

  /**
   * Firebase resolver customization.
   * If not provided, resolveMedia uses firebase/storage dynamic import and getStorage() default app.
   */
  firebase?: {
    /** Provide a Firebase Storage instance to avoid relying on default app. */
    storage?: any;
  };

  /**
   * Static asset base override. Defaults to import.meta.env.BASE_URL (Vite).
   * Examples: "/" or "/myapp/"
   */
  staticBaseUrl?: string;

  /** Optional URL rewrite hook (e.g., CDN mapping). */
  rewriteUrl?: (url: string, ref: MediaRefV1) => string;
}

/* ------------------------------------------------------------------ */
/* Global fallback (ExportBridge / window installers can set this)     */
/* ------------------------------------------------------------------ */

type GlobalMediaRegistrySlot = {
  registryResolver?: (ref: any) => Promise<string | { url: string; mime?: string; meta?: Record<string, any> }>;
};

function getGlobalRegistryResolver(): ResolveMediaInstallOptions["registryResolver"] | undefined {
  const g = globalThis as any;
  const slot = (g.__oc_media_registry || {}) as GlobalMediaRegistrySlot;
  return slot.registryResolver as any;
}

let _installed: ResolveMediaInstallOptions = {};

export function installResolveMedia(opts: ResolveMediaInstallOptions): () => void {
  const prev = _installed;
  _installed = { ...prev, ...opts };
  return () => {
    _installed = prev;
  };
}

function getStaticBaseUrl(local?: ResolveMediaInstallOptions): string {
  const base =
    local?.staticBaseUrl ??
    _installed.staticBaseUrl ??
    // Vite base (string like "/" or "/subdir/")
    ((import.meta as any)?.env?.BASE_URL as string | undefined) ??
    "/";

  return base.endsWith("/") ? base : `${base}/`;
}

function getOrigin(): string {
  // Works in browser and in some export runtimes.
  if (typeof window !== "undefined" && window?.location?.origin) return window.location.origin;
  // Fallback for non-browser contexts (keeps URL constructor usable)
  return "http://localhost";
}

function toAbsoluteStaticUrl(path: string, baseUrl: string): string {
  const p = String(path ?? "").trim();
  if (!p) throw new Error("StaticMediaRefV1.path is empty");

  // Already absolute-ish (http/blob/data)
  if (/^(https?:|blob:|data:)/i.test(p)) return p;

  const origin = getOrigin();

  // If leading slash, resolve from origin (ignores Vite base by design)
  if (p.startsWith("/")) return new URL(p, origin).toString();

  // Otherwise resolve relative to Vite BASE_URL
  return new URL(p, new URL(baseUrl, origin)).toString();
}

function normalizeGsToHttp(ref: GcsMediaRefV1): { url: string; meta?: Record<string, any> } {
  // NOTE: This is a public URL form. Private buckets will require signed URLs
  // (which should be handled via provider="registry" or a custom resolver).
  const gsUri = (ref.gsUri || "").trim();
  if (gsUri) {
    const m = /^gs:\/\/([^/]+)\/(.+)$/i.exec(gsUri);
    if (m) {
      const bucket = m[1];
      const object = m[2];
      const url = `https://storage.googleapis.com/${encodeURIComponent(bucket)}/${object
        .split("/")
        .map(encodeURIComponent)
        .join("/")}`;
      return { url, meta: { bucket, object, via: "gsUri->https" } };
    }
  }

  const bucket = (ref.bucket || "").trim();
  const object = (ref.object || "").trim();
  if (bucket && object) {
    const url = `https://storage.googleapis.com/${encodeURIComponent(bucket)}/${object
      .split("/")
      .map(encodeURIComponent)
      .join("/")}`;
    return { url, meta: { bucket, object, via: "bucket+object->https" } };
  }

  throw new Error("GcsMediaRefV1 requires gsUri OR (bucket + object)");
}

async function resolveFirebaseUrl(
  ref: FirebaseMediaRefV1,
  local?: ResolveMediaInstallOptions,
): Promise<{ url: string }> {
  // Dynamic import to avoid hard coupling in builds where firebase isn't configured.
  const mod = await import("firebase/storage");
  const getStorage = mod.getStorage as any;
  const storageRef = mod.ref as any;
  const getDownloadURL = mod.getDownloadURL as any;

  const storage = local?.firebase?.storage ?? _installed.firebase?.storage ?? getStorage();
  const r = storageRef(storage, ref.path);
  const url = await getDownloadURL(r);
  return { url };
}

function applyRewrite(url: string, ref: MediaRefV1, local?: ResolveMediaInstallOptions): string {
  const rewrite = local?.rewriteUrl ?? _installed.rewriteUrl;
  return rewrite ? rewrite(url, ref) : url;
}

export async function resolveMedia(
  ref: MediaRefV1,
  local?: ResolveMediaInstallOptions,
): Promise<ResolvedMediaV1> {
  if (!ref || (ref as any).v !== 1) throw new Error("resolveMedia requires MediaRefV1 (v=1)");

  switch (ref.provider) {
    case "url": {
      const r = ref as UrlMediaRefV1;
      const url = applyRewrite(r.url, ref, local);
      return {
        v: 1,
        kind: ref.kind,
        provider: ref.provider,
        url,
        ...(ref.mime ? { mime: ref.mime } : {}),
        ref,
      };
    }

    case "static": {
      const r = ref as StaticMediaRefV1;
      const baseUrl = getStaticBaseUrl(local);
      const url = applyRewrite(toAbsoluteStaticUrl(r.path, baseUrl), ref, local);
      return {
        v: 1,
        kind: ref.kind,
        provider: ref.provider,
        url,
        ...(ref.mime ? { mime: ref.mime } : {}),
        ref,
        meta: { baseUrl },
      };
    }

    case "firebase": {
      const out = await resolveFirebaseUrl(ref as FirebaseMediaRefV1, local);
      const url = applyRewrite(out.url, ref, local);
      return {
        v: 1,
        kind: ref.kind,
        provider: ref.provider,
        url,
        ...(ref.mime ? { mime: ref.mime } : {}),
        ref,
      };
    }

    case "gcs": {
      const out = normalizeGsToHttp(ref as GcsMediaRefV1);
      const url = applyRewrite(out.url, ref, local);
      return {
        v: 1,
        kind: ref.kind,
        provider: ref.provider,
        url,
        ...(ref.mime ? { mime: ref.mime } : {}),
        ref,
        ...(out.meta ? { meta: out.meta } : {}),
      };
    }

    case "registry": {
      const rr = local?.registryResolver ?? _installed.registryResolver ?? getGlobalRegistryResolver();

      if (!rr) {
        const r = ref as RegistryMediaRefV1;
        const ns = r.registry ? ` registry="${r.registry}"` : "";
        throw new Error(
          `No registryResolver installed for MediaRefV1(provider="registry", key="${r.key}"${ns}). ` +
            `Install via installResolveMedia({ registryResolver }) or provide local resolver.`,
        );
      }

      const out = await rr(ref as RegistryMediaRefV1);

      const rawUrl = typeof out === "string" ? out : out.url;
      const url = applyRewrite(rawUrl, ref, local);

      // Deterministic precedence: ref.mime wins, else resolver mime
      const resolverMime = typeof out === "string" ? undefined : out.mime;
      const finalMime = ref.mime || resolverMime;

      const meta = typeof out === "string" ? undefined : out.meta;

      return {
        v: 1,
        kind: ref.kind,
        provider: ref.provider,
        url,
        ...(finalMime ? { mime: finalMime } : {}),
        ref,
        ...(meta ? { meta } : {}),
      };
    }

    default: {
      const unsupportedRef = ref as MediaRefV1;
      throw new Error(`Unsupported media provider: ${unsupportedRef.provider}`);
    }
  }
}
